// Single-purpose pages for password reset request + completion.
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Mail, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import { API } from '../App';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Backend is non-enumerating; treat all responses as success unless 429
      if (err.response?.status === 429) {
        toast.error('Please wait a bit before trying again');
      } else {
        setSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-emerald-50 flex flex-col" data-testid="forgot-password-page">
      <div className="p-4 flex items-center justify-center">
        <Heart className="text-emerald-600" size={28} />
        <span className="ml-2 text-xl font-semibold text-[#7D202B]">Veyonika</span>
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <button
            onClick={() => navigate('/auth')}
            className="flex items-center gap-1 text-sm text-gray-500 mb-3"
            data-testid="forgot-back-btn"
          >
            <ArrowLeft size={16} /> Back to sign in
          </button>
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            {sent ? (
              <div className="text-center" data-testid="forgot-sent-state">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Check your inbox</h2>
                <p className="text-sm text-gray-500 mb-5">
                  If an account exists for <strong>{email}</strong>, we've sent a reset link.
                  The link expires in 30 minutes.
                </p>
                <button
                  onClick={() => navigate('/auth')}
                  className="w-full py-3 rounded-xl text-white bg-emerald-500 hover:bg-emerald-600"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-gray-800 mb-1 text-center">Forgot password?</h2>
                <p className="text-gray-500 text-sm mb-5 text-center">We'll email you a reset link.</p>
                <form onSubmit={submit} className="space-y-4">
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 text-base"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 rounded-xl text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="forgot-submit-btn"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    Send reset link
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Invalid or missing reset token');
      return;
    }
    if (pw.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success('Password updated. You can sign in now.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-emerald-50 flex flex-col" data-testid="reset-password-page">
      <div className="p-4 flex items-center justify-center">
        <Heart className="text-emerald-600" size={28} />
        <span className="ml-2 text-xl font-semibold text-[#7D202B]">Veyonika</span>
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            {done ? (
              <div className="text-center" data-testid="reset-done-state">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Password updated</h2>
                <button
                  onClick={() => navigate('/auth')}
                  className="w-full py-3 rounded-xl text-white bg-emerald-500 hover:bg-emerald-600 mt-4"
                  data-testid="reset-back-to-signin-btn"
                >
                  Sign in
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-gray-800 mb-1 text-center">Set a new password</h2>
                <p className="text-gray-500 text-sm mb-5 text-center">At least 8 characters.</p>
                <form onSubmit={submit} className="space-y-4">
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      placeholder="New password"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 text-base"
                      data-testid="reset-pw-input"
                    />
                  </div>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="password"
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      placeholder="Confirm new password"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 text-base"
                      data-testid="reset-pw2-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 rounded-xl text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="reset-submit-btn"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    Update password
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
